import React, { useState, useEffect, useCallback } from 'react';
import LoginScreen from './components/LoginScreen';
import Header from './components/Header';
import Navigation from './components/Navigation';
import AdvanceCollection from './components/AdvanceCollection';
import StoreMaster from './components/StoreMaster';
import GoldMaster from './components/GoldMaster';
import UserMaster from './components/UserMaster';
import UserStoreMapping from './components/UserStoreMapping';
import AdvanceSummaryReport from './components/AdvanceSummaryReport';
import PaymodeReport from './components/PaymodeReport';
import DateWisePaymentReport from './components/DateWisePaymentReport';
import Dashboard from './components/Dashboard';
import { getStoredUser, getAuthToken, clearAuthTokens, isSessionExpired, api } from './services/api';
import { notifyWarning } from './utils/notify';

function App() {
  const hasValidSession = (user = getStoredUser()) => {
    const token = getAuthToken();
    return Boolean(user && token && !isSessionExpired(user));
  };

  const formatApiDate = (value) => {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}/${month}/${day}`;
  };

  const getDefaultAdvanceQuery = (user) => ({
    storeCode: user?.store || '',
    startDate: formatApiDate(new Date()),
    endDate: formatApiDate(new Date()),
  });

  const isAdminUserRole = (role) => {
    const normalizedRole = (role || '').toLowerCase();
    return ['super admin', 'application admin', 'admin', 'system administrator'].includes(normalizedRole);
  };

  const isSystemAdminUserRole = (role) => {
    const normalizedRole = (role || '').toLowerCase();
    return ['super admin', 'admin', 'system administrator'].includes(normalizedRole);
  };

  const isSuperAdminUserRole = (role) => {
    const normalizedRole = (role || '').toLowerCase();
    return normalizedRole === 'super admin';
  };

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingStores, setIsLoadingStores] = useState(false);
  
  // Initialize data
  const [stores, setStores] = useState([]);

  const paymodes = ['Cash', 'Card', 'UPI', 'Cheque'];

  const [goldRates, setGoldRates] = useState([]);
  const [goldRatesLastSyncedAt, setGoldRatesLastSyncedAt] = useState(null);
    const [latestGoldRate, setLatestGoldRate] = useState(null);
    const [dashboardStats, setDashboardStats] = useState(null);
  const [isLoadingDashboard, setIsLoadingDashboard] = useState(false);

  const [advances, setAdvances] = useState([]);
  const [isLoadingAdvances, setIsLoadingAdvances] = useState(false);
  const [advanceQuery, setAdvanceQuery] = useState(getDefaultAdvanceQuery(null));

  const performLogout = useCallback((message = '') => {
    clearAuthTokens();
    setIsLoggedIn(false);
    setCurrentUser(null);
    setActiveSection('dashboard');
    setAdvances([]);
    if (message) {
      notifyWarning(message, 'Session');
    }
  }, []);

  // Check for existing session on app load
  useEffect(() => {
    const checkExistingSession = () => {
      const storedUser = getStoredUser();

      if (hasValidSession(storedUser)) {
        // User is logged in, restore session
        setCurrentUser(storedUser);
        setAdvanceQuery(getDefaultAdvanceQuery(storedUser));
        setIsLoggedIn(true);
      } else {
        clearAuthTokens();
      }

      setIsLoading(false);
    };

    checkExistingSession();
  }, []);

  // Auto logout when backend returns unauthorized for any request
  useEffect(() => {
    const handleSessionExpired = () => {
      if (isLoggedIn) {
        performLogout('Session expired. Please login again.');
      }
    };

    window.addEventListener('auth:session-expired', handleSessionExpired);
    return () => {
      window.removeEventListener('auth:session-expired', handleSessionExpired);
    };
  }, [isLoggedIn, performLogout]);

  // Proactive expiry check based on expiresAt from login response
  useEffect(() => {
    if (!isLoggedIn || !currentUser?.expiresAt) {
      return;
    }

    const checkExpiry = () => {
      if (isSessionExpired(currentUser)) {
        performLogout('Session expired. Please login again.');
      }
    };

    checkExpiry();
    const intervalId = setInterval(checkExpiry, 30000);
    return () => clearInterval(intervalId);
  }, [isLoggedIn, currentUser, performLogout]);

  // Fetch stores when user logs in
  useEffect(() => {
    const fetchStores = async () => {
      if (!isLoggedIn) return;
      
      setIsLoadingStores(true);
      try {
        const response = await api.stores.getAll();
        
        if (response.success && response.data) {
          // Map API response to app format
          const mappedStores = response.data.map(store => ({
            id: store.id,
            code: store.storeCode,
            name: store.storeName,
            address: `${store.address}, ${store.city}`,
            location: store.location,
            city: store.city,
            state: store.state,
            pincode: store.pincode,
            contactPerson: store.contactPerson,
            contactPhone: store.contactPhone,
            contactEmail: store.contactEmail,
            isActive: store.isActive
          }));
          
          setStores(mappedStores);
          console.log('Stores loaded:', mappedStores);
        }
      } catch (error) {
        console.error('Error fetching stores:', error);
        // Keep empty stores array on error
      } finally {
        setIsLoadingStores(false);
      }
    };

    fetchStores();
  }, [isLoggedIn]);

  const mapApiGoldRateToUi = (apiRate) => ({
    date: new Date(apiRate.effectiveDate || apiRate.createdAt || Date.now()).toISOString().split('T')[0],
    time: new Date(apiRate.createdAt || apiRate.effectiveDate || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    store: apiRate.storeCode || 'ALL',
    storeName: apiRate.storeName || '',
    r18: Number(apiRate.rate18K || 0),
    r22: Number(apiRate.rate22K || 0),
    r24: Number(apiRate.rate24K || 0),
    remarks: apiRate.remarks || '',
  });

  const fetchGoldRates = useCallback(async () => {
    if (!isLoggedIn || !currentUser) return;

    try {
      const isSystemAdmin = isAdminUserRole(currentUser.role);
      const params = !isSystemAdmin && currentUser.store
        ? { storeCode: currentUser.store }
        : {};

      const response = await api.goldRates.getAll(params);
      if (!response.success) {
        return;
      }

      const rows = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.data)
          ? response.data.data
          : [];

      setGoldRates(rows.map(mapApiGoldRateToUi));
      setGoldRatesLastSyncedAt(response.timestamp || new Date().toISOString());
    } catch (error) {
      console.error('Error fetching gold rates:', error);
    }
  }, [isLoggedIn, currentUser]);

  // Fetch gold rates from API when user logs in
  useEffect(() => {
    fetchGoldRates();
  }, [fetchGoldRates]);

  const fetchLatestGoldRate = useCallback(async () => {
    if (!isLoggedIn || !currentUser) return;

    try {
      let effectiveStoreCode = (currentUser.store || '').trim();

      if (!effectiveStoreCode && currentUser?.id) {
        const queryParams = `?isActive=true&userId=${currentUser.id}`;
        const userStoresResponse = await api.userStores.getAll(queryParams);
        const mappedStores = Array.isArray(userStoresResponse?.data) ? userStoresResponse.data : [];
        effectiveStoreCode = (mappedStores[0]?.storeCode || '').trim();
      }

      if (!effectiveStoreCode) {
        setLatestGoldRate(null);
        return;
      }

      const response = await api.goldRates.getLatest(effectiveStoreCode);
      if (response?.success && response?.data) {
        setLatestGoldRate(response.data);
      }
    } catch (error) {
      console.error('Error fetching latest gold rate:', error);
    }
  }, [isLoggedIn, currentUser]);

  useEffect(() => {
    fetchLatestGoldRate();
  }, [fetchLatestGoldRate]);

  const mapApiAdvanceToUi = (apiAdvance) => ({
    transNo: apiAdvance.advanceNumber,
    date: apiAdvance.advanceDate,
    time: new Date(apiAdvance.advanceDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    mobile: apiAdvance.customerMobile,
    name: apiAdvance.customerName,
    address: apiAdvance.customerAddress || '',
    store: apiAdvance.storeCode,
    storeName: apiAdvance.storeName || '',
    goldRate24K: Number(apiAdvance.goldRate24K ?? apiAdvance.rate24K ?? 0),
    totalAmount: Number(apiAdvance.amountReceived || 0),
    breakdown: (apiAdvance.payments || []).reduce((acc, payment) => {
      acc[payment.paymentMode] = Number(payment.amount || 0);
      return acc;
    }, {}),
    orderNumber: apiAdvance.orderNumber || '',
  });

  const fetchAdvances = useCallback(async (filters = advanceQuery) => {
    if (!isLoggedIn) return;

    setIsLoadingAdvances(true);
    try {
      setAdvanceQuery(filters);
      const params = {
        ...(filters.storeCode ? { storeCode: filters.storeCode } : {}),
        ...(filters.startDate ? { startDate: filters.startDate } : {}),
        ...(filters.endDate ? { endDate: filters.endDate } : {}),
      };
      const response = await api.advances.getAll(params);

      if (response.success) {
        const rawRows = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.data)
            ? response.data.data
            : [];
        const mappedAdvances = rawRows.map(mapApiAdvanceToUi);
        setAdvances(mappedAdvances);
      }
    } catch (error) {
      console.error('Error fetching advance collections:', error);
    } finally {
      setIsLoadingAdvances(false);
    }
  }, [advanceQuery, isLoggedIn]);

  // Fetch advance collections when user logs in
  useEffect(() => {
    if (isLoggedIn) {
      fetchAdvances(advanceQuery);
    }
  }, [advanceQuery, fetchAdvances, isLoggedIn]);

  const fetchDashboardStats = useCallback(async () => {
    if (!isLoggedIn) return;

    setIsLoadingDashboard(true);
    try {
      const response = await api.dashboard.getStats();
      if (response.success && response.data) {
        setDashboardStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setIsLoadingDashboard(false);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn) {
      fetchDashboardStats();
    }
  }, [isLoggedIn, fetchDashboardStats]);

  // Show loading screen while checking session
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jewel-red mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const handleLogin = (user) => {
    setAdvanceQuery(getDefaultAdvanceQuery(user));
    setCurrentUser(user);
    setActiveSection('dashboard');
    setIsLoggedIn(true);
  };

  const handleLogout = () => {
    performLogout();
  };

  const handleSaveAdvance = async (advanceData) => {
    const selectedRate = [...goldRates].reverse().find((rate) => rate.store === advanceData.store || rate.store === 'ALL');
    const payments = Object.entries(advanceData.breakdown)
      .filter(([_, amount]) => Number(amount) > 0)
      .map(([paymentMode, amount]) => ({
        paymentMode,
        amount: Number(amount),
        transactionRef: '',
        remarks: '',
      }));

    const payload = {
      storeCode: advanceData.store,
      advanceDate: new Date().toISOString(),
      customerName: advanceData.name,
      customerMobile: advanceData.mobile,
      customerEmail: '',
      customerAddress: advanceData.address || '',
      payments,
      goldRate24K: Number(advanceData.goldRate24K || selectedRate?.r24 || 0),
      orderNumber: advanceData.orderNumber || '',
      remarks: '',
    };

    const response = await api.advances.create(payload);

    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to create advance collection');
    }

    await fetchAdvances(advanceQuery);
    return response;
  };

  const handleAddStore = (storeData) => {
    setStores([...stores, storeData]);
  };

  const handleSaveGoldRate = async (rateData) => {
    const payload = {
      storeCode: rateData.store,
      rate22K: Number(rateData.r22),
      rate24K: Number(rateData.r24),
      rate18K: Number(rateData.r18),
      effectiveDate: new Date(`${rateData.date}T00:00:00`).toISOString(),
      remarks: rateData.remarks || '',
    };

    const response = await api.goldRates.create(payload);

    if (!response?.success) {
      throw new Error(response.message || 'Failed to add gold price');
    }

    await fetchGoldRates();
    return response;
  };

  const renderSection = () => {
    // Check if user is admin
    const isAdminUser = ['super admin', 'application admin', 'admin', 'system administrator'].includes((currentUser?.role || '').toLowerCase());

    switch (activeSection) {
      case 'dashboard':
        return isAdminUser ? (
          <Dashboard
            currentUser={currentUser}
            dashboardStats={dashboardStats}
            isLoadingDashboard={isLoadingDashboard}
            onRefreshDashboard={fetchDashboardStats}
          />
        ) : (
          <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
            <div className="text-center py-16">
              <div className="mb-6">
                <i className="fas fa-hand-wave text-6xl text-jewel-red opacity-80"></i>
              </div>
              <h2 className="text-3xl font-bold text-gray-800 mb-2">
                Welcome, {currentUser?.fullName || 'User'}!
              </h2>
              <p className="text-gray-600 text-lg mb-8">
                You're all set and ready to start collecting advances.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto mt-10">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <i className="fas fa-plus-circle text-3xl text-blue-600 mb-3"></i>
                  <h3 className="font-semibold text-gray-800 mb-2">Create New Collection</h3>
                  <p className="text-sm text-gray-600">Add new advance collection entries to track customer advances</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <i className="fas fa-chart-bar text-3xl text-green-600 mb-3"></i>
                  <h3 className="font-semibold text-gray-800 mb-2">View Collections</h3>
                  <p className="text-sm text-gray-600">Track and view all advance collections from your store</p>
                </div>
              </div>
            </div>
          </div>
        );
      case 'advance':
        return (
          <AdvanceCollection
            currentUser={currentUser}
            stores={stores}
            advances={advances}
            onSaveAdvance={handleSaveAdvance}
            paymodes={paymodes}
            isLoadingAdvances={isLoadingAdvances}
            advanceQuery={advanceQuery}
            onRefreshAdvances={fetchAdvances}
          />
        );
      case 'store-master':
        return (
          <StoreMaster
            stores={stores}
            onAddStore={handleAddStore}
          />
        );
      case 'gold-master':
        return (
          <GoldMaster
            currentUser={currentUser}
            stores={stores}
            goldRates={goldRates}
            lastSyncedAt={goldRatesLastSyncedAt}
            onSaveGoldRate={handleSaveGoldRate}
          />
        );
      case 'user-master':
        return isSystemAdminUserRole(currentUser?.role) ? <UserMaster /> : null;
      case 'user-store-mapping':
        return isSuperAdminUserRole(currentUser?.role) ? <UserStoreMapping stores={stores} /> : null;
      case 'report-adv-summary':
        return (
          <AdvanceSummaryReport
            currentUser={currentUser}
            stores={stores}
          />
        );
      case 'report-paymode':
        return (
          <PaymodeReport
            currentUser={currentUser}
            stores={stores}
            advances={advances}
            paymodes={paymodes}
          />
        );
      case 'report-date-wise':
        return (
          <DateWisePaymentReport
            currentUser={currentUser}
          />
        );
      default:
        return null;
    }
  };

  if (!isLoggedIn || !hasValidSession(currentUser)) {
    return <LoginScreen onLogin={handleLogin} />;
  }

  return (
    <div className="bg-gray-50 font-sans h-screen flex flex-col overflow-hidden">
      <Header
        currentUser={currentUser}
        stores={stores}
        goldRates={goldRates}
          latestGoldRate={latestGoldRate}
        onLogout={handleLogout}
        onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      />
      <div className="flex flex-1 overflow-hidden">
        <Navigation
          currentUser={currentUser}
          activeSection={activeSection}
          onSectionChange={(section) => {
            setActiveSection(section);
            setIsMobileMenuOpen(false);
          }}
          isMobileMenuOpen={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
        />
        <main className="flex-1 overflow-y-auto p-4 md:p-8">
          {renderSection()}
        </main>
      </div>
    </div>
  );
}

export default App;
